import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Activity, CalendarDays, CheckCircle2, Clock, Flag, ListChecks, Plus, Search, UserRound, Users } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { PatientDialog } from "@/components/PatientDialog";
import { RemindersPanel } from "@/components/RemindersPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { ageFromBirth, fmtDate } from "@/lib/aba";
import { PATIENTS } from "@/constants/testIds";

export default function DashboardPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const { data: patients = [], isLoading, isError } = useQuery({
    queryKey: ["patients"],
    queryFn: async () => (await api.get("/patients")).data,
  });

  const filtered = patients.filter((p) => `${p.nome} ${p.cognome}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <AppShell breadcrumbs={[{ label: "Pazienti" }]}>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-sm font-medium text-primary mb-1">Dashboard</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Pazienti</h1>
          <p className="text-muted-foreground mt-1">Seleziona un paziente per registrare sessioni e consultare i grafici ABA.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca paziente…" className="pl-9 h-11 w-full sm:w-64 bg-card" data-testid={PATIENTS.searchInput} />
          </div>
          <Button className="h-11 gap-2" onClick={() => setOpen(true)} data-testid={PATIENTS.newButton}>
            <Plus className="h-4 w-4" /> Nuovo paziente
          </Button>
        </div>
      </div>

      <div className="mb-6"><RemindersPanel compact /></div>

      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-44 rounded-xl" />)}
        </div>
      )}
      {isError && <p className="text-destructive">Impossibile caricare i pazienti. Ricarica la pagina.</p>}

      {!isLoading && !isError && filtered.length === 0 && (
        <Card className="border-dashed" data-testid={PATIENTS.emptyState}>
          <CardContent className="py-16 flex flex-col items-center text-center gap-3">
            <span className="h-14 w-14 rounded-2xl bg-accent flex items-center justify-center"><Users className="h-7 w-7 text-primary" /></span>
            <h3 className="font-heading text-xl font-semibold">{search ? "Nessun paziente trovato" : "Nessun paziente ancora"}</h3>
            <p className="text-muted-foreground max-w-sm">{search ? "Prova a modificare il termine di ricerca." : "Aggiungi il primo paziente per iniziare a raccogliere i dati degli operanti verbali e della performance visiva."}</p>
            {!search && <Button className="mt-2 gap-2" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Aggiungi paziente</Button>}
          </CardContent>
        </Card>
      )}

      {filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid={PATIENTS.grid}>
          {filtered.map((p, i) => {
            const age = ageFromBirth(p.data_nascita);
            const progress = p.n_azioni ? Math.round((p.n_acquisite / p.n_azioni) * 100) : 0;
            return (
              <motion.div key={p.patient_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: Math.min(i * 0.04, 0.3) }}>
                <Card
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/pazienti/${p.patient_id}`)}
                  onKeyDown={(e) => e.key === "Enter" && navigate(`/pazienti/${p.patient_id}`)}
                  className="group cursor-pointer transition-shadow duration-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring h-full"
                  data-testid={`patient-card-${p.patient_id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="h-11 w-11 rounded-xl bg-accent text-primary flex items-center justify-center shrink-0"><UserRound className="h-5 w-5" /></span>
                        <div className="min-w-0">
                          <h3 className="font-heading font-semibold text-lg leading-tight truncate">{p.nome} {p.cognome}</h3>
                          <p className="text-xs text-muted-foreground truncate">{age !== null ? `${age} anni` : "Età non indicata"}{p.diagnosi ? ` · ${p.diagnosi}` : ""}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {p.n_in_ritardo > 0 && <Badge className="bg-rose-50 text-rose-900 border border-rose-200 hover:bg-rose-50 gap-1" data-testid={`patient-late-${p.patient_id}`}><Flag className="h-3 w-3" /> {p.n_in_ritardo} in ritardo</Badge>}
                        {p.n_promemoria > 0 && <Badge className="bg-rose-50 text-rose-900 border border-rose-200 hover:bg-rose-50 gap-1" data-testid={`patient-reminders-${p.patient_id}`}><Clock className="h-3 w-3" /> {p.n_promemoria} da riprendere</Badge>}
                        {p.n_acquisite > 0 && <Badge className="bg-emerald-50 text-emerald-900 border border-emerald-200 hover:bg-emerald-50">{p.n_acquisite} acquisite</Badge>}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-5">
                      <Stat icon={ListChecks} label="Azioni" value={p.n_azioni} />
                      <Stat icon={Activity} label="Sessioni" value={p.n_sessioni} />
                      <Stat icon={CheckCircle2} label="Acquisite" value={`${progress}%`} />
                    </div>
                    <div className="mt-4 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${progress}%` }} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Ultima sessione: {fmtDate(p.ultima_sessione)}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      <PatientDialog open={open} onOpenChange={setOpen} onSaved={(p) => navigate(`/pazienti/${p.patient_id}`)} />
    </AppShell>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg bg-muted/60 px-3 py-2">
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</p>
    <p className="font-heading font-semibold text-lg tabular">{value}</p>
  </div>
);
